import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getJkmQueries, updateJkmQuery } from "../../services/jkmQueryService";
import { toast } from "react-toastify";

export const JKM_QUERY_STATUSES = [
  "New",
  "Contacted",
  "In Progress",
  "Completed",
  "Not Reachable",
  "Cancelled",
];

const initialState = {
  queries: [],
  status: "idle",
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

// ── Thunks ──────────────────────────────────────────────────────────────────

export const fetchJkmQueries = createAsyncThunk(
  "jkmQueries/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getJkmQueries(params);
      return response;
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to fetch JKM queries";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const updateJkmQueryThunk = createAsyncThunk(
  "jkmQueries/update",
  async ({ id, queryData }, { rejectWithValue }) => {
    try {
      const response = await updateJkmQuery(id, queryData);
      toast.success(response?.message || "Query updated successfully!");
      return { id, updatedData: response?.data || response, queryData };
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to update query";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const jkmQuerySlice = createSlice({
  name: "jkmQueries",
  initialState,
  reducers: {
    clearJkmError: (state) => {
      state.error = null;
    },
    resetJkmQueryState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchJkmQueries
      .addCase(fetchJkmQueries.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchJkmQueries.fulfilled, (state, action) => {
        state.status = "succeeded";
        const raw = action.payload?.queries || action.payload?.data || [];
        state.queries = Array.isArray(raw) ? raw : [];
        state.pagination = action.payload?.pagination || {
          total: state.queries.length,
          page: 1,
          limit: 10,
          totalPages: 1,
        };
        state.error = null;
      })
      .addCase(fetchJkmQueries.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.queries = [];
      })

      // updateJkmQueryThunk
      .addCase(updateJkmQueryThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateJkmQueryThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { id, updatedData, queryData } = action.payload;
        if (id && Array.isArray(state.queries)) {
          const idx = state.queries.findIndex((q) => q._id === id);
          if (idx !== -1) {
            state.queries[idx] = {
              ...state.queries[idx],
              ...updatedData,
              ...queryData,
            };
          }
        }
        state.error = null;
      })
      .addCase(updateJkmQueryThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearJkmError, resetJkmQueryState } = jkmQuerySlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectJkmQueries = (state) => state.jkmQueries.queries;
export const selectJkmQueryStatus = (state) => state.jkmQueries.status;
export const selectJkmQueryPagination = (state) => state.jkmQueries.pagination;
export const selectJkmQueryError = (state) => state.jkmQueries.error;

export default jkmQuerySlice.reducer;